import React, { useState } from "react";
import {
  TextField,
  CircularProgress,
  Button,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  RadioGroup,
  Radio,
  Grid,
  Typography,
  Container,
  Paper,
  Box,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { FormControl, FormHelperText } from "@mui/material";
import Navbar from "../../../globals/components/Navbar";
import GuestNavbar from "../GuestNavbar";
import InputMask from "react-input-mask";
import { useCallback, useEffect, useRef } from "react";
import { apiClient } from "../../../globals/utils/api";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { is_prod } from "../../../globals/utils/constants";
import debounce from "lodash.debounce";
import { EMAIL_DEBOUNCE_TIMEOUT } from "../../../globals/utils/constants";
import { extractErrorMessage } from "../../../globals/utils/error-handler";
import { loadGoogleMaps } from "../../../globals/utils/googleMapsLoader";
import SupportRequestModal from "../../../components/SupportRequestModal";
import { createSupportRequest } from "../../../services/supportRequestService";
import CalculatorIcon from "@mui/icons-material/Calculate";
import EmailIcon from "@mui/icons-material/Email";
import PremiumCalculator from "../PremiumCalculatorPublic";
import SupportContactDialog from "../../../components/SupportContactDialog";

const steps = [
  "Basic Information",
  "Detailed Information",
  "Obligee Information",
];

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
};

// Google Autocomplete Component
const GoogleAutoComplete = ({ placeholder, callback }) => {
  const autoCompleteRef = useRef();
  const inputRef = useRef();
  const [loaded, setLoaded] = useState(false);

  const loadGoogleMapsScript = useCallback(() => {
    loadGoogleMaps()
      .then(() => setLoaded(true))
      .catch((e) => console.error(e));
  }, []);

  useEffect(() => {
    loadGoogleMapsScript();
  }, [loadGoogleMapsScript]);

  useEffect(() => {
    if (loaded && window.google) {
      autoCompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          fields: ["address_components", "geometry"],
          types: ["address"], // better for full addresses
        }
      );

      autoCompleteRef.current.addListener("place_changed", () => {
        const place = autoCompleteRef.current.getPlace();

        if (!place || !place.address_components) {
          console.error("No valid place returned or missing address components.");
          return;
        }

        let address = "",
          city = "",
          state = "",
          zipCode = "";

        place.address_components.forEach((component) => {
          const types = component.types;

          if (types.includes("street_number")) {
            address = `${component.long_name} ${address}`;
          }
          if (types.includes("route")) {
            address += component.long_name;
          }

          // City fallbacks
          if (types.includes("locality")) {
            city = component.long_name;
          }
          if (!city && (types.includes("sublocality") || types.includes("sublocality_level_1"))) {
            city = component.long_name;
          }
          if (!city && types.includes("administrative_area_level_2")) {
            city = component.long_name;
          }

          if (types.includes("administrative_area_level_1")) {
            state = component.long_name; // ✅ full state name
          }

          if (types.includes("postal_code")) {
            zipCode = component.long_name;
          }
        });

        // Fallback: geocoder if city/state missing
        if ((!city || !state) && place.geometry?.location) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: place.geometry.location }, (results, status) => {
            if (status === "OK" && results[0]) {
              results[0].address_components.forEach((component) => {
                const types = component.types;
                if (!city && types.includes("locality")) {
                  city = component.long_name;
                }
                if (!state && types.includes("administrative_area_level_1")) {
                  state = component.long_name; // ✅ full name
                }
              });
              if (callback) callback(address, city, state, zipCode);
            }
          });
        } else {
          if (callback) callback(address, city, state, zipCode);
        }
      });
    }
  }, [loaded, callback]);

  return (
    <TextField
      variant="outlined"
      fullWidth
      placeholder={placeholder}
      inputRef={inputRef}
      sx={{ marginBottom: "8px" }}
    />
  );
};

const VermostBlanketBondRequestForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isGuestBlanketRequest, setIsGuestBlanketRequest] = useState(
    (location.pathname || "").startsWith("/vermost-blanket-bond-request-guest")
  );
  const [openCalculator, setOpenCalculator] = useState(false);
  const [openSupport, setOpenSupport] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const {
    handleSubmit,
    control,
    watch,
    getValues,
    setValue,
    reset,
    formState: { errors },
    trigger,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      obligeeId: null,
    },
  });
  const residencyStatus = watch("residencyStatus");
  const [otherResidency, setOtherResidency] = useState("");
  const [allCities, setAllCities] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [state, setState] = useState(null);
  const [allCounties, setAllCounties] = useState([]);
  const [lawfirm, setLawfirm] = useState(null);
  const [profGuardianRequestor, setProfGuardianRequestor] = useState(null);
  const [obligees, setObligees] = useState([]);
  const [loadingObligees, setLoadingObligees] = useState(false);
  const [openObligeeDialog, setOpenObligeeDialog] = useState(false);
  const lawfirmState = useSelector((state) => state.lawfirm);
  const [newObligee, setNewObligee] = useState({
    name: "",
    street: "",
    city: "",
    state: "",
    zip: "",
  });

  //Redux State
  const [loadingCounties, setLoadingCounties] = useState(false);
  const userdetails = useSelector((state) => state.auth.user);
  const { role } = useSelector((state) => state.auth);
  const pgId = useSelector((state) => state.pg.pgId);
  const [isLoading, setIsLoading] = useState(false);

  // Email existence state
  const [emailExists, setEmailExists] = useState(false);
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const emailDebounceRef = useRef();

  const { cache_id } = useParams();
  const [cacheId, setCacheId] = useState(cache_id || "");

  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [submissionError, setSubmissionError] = useState("");
  const [failedBondType, setFailedBondType] = useState("");

  // Instant Hydration from resumedBondPayload
  useEffect(() => {
    const savedPayload = localStorage.getItem("resumedBondPayload");
    if (savedPayload) {
      try {
        const parsed = JSON.parse(savedPayload);
        let body = parsed.request_body;
        if (typeof body === "string") body = JSON.parse(body);
        if (body && body.state) {
          reset(body.state);
        }
        if (body && typeof body.activeStep === 'number') {
          setActiveStep(body.activeStep);
        }
        toast.info("Restored bond application from support request.");
        localStorage.removeItem("resumedBondPayload");
      } catch (e) { }
    }
  }, [reset]);

  useEffect(() => {
    const loadCache = async () => {
      if (!cache_id) return;
      try {
        const data = await apiClient(`/bond-cache/${cache_id}/`);
        let body = data?.request_body;
        if (typeof body === "string") {
          try { body = JSON.parse(body); } catch (_) { }
        }
        if (body?.state) {
          reset(body.state);
        }
        if (typeof body?.activeStep === "number") {
          setActiveStep(body.activeStep);
        }
      } catch (e) {
        toast.error("Failed to load saved progress");
      }
    };
    loadCache();
  }, [cache_id, reset]);

  // Debounced function to check email existence
  if (!emailDebounceRef.current) {
    emailDebounceRef.current = debounce(async (email, setValue) => {
      if (!email) {
        setEmailExists(false);
        setEmailChecking(false);
        setEmailDialogOpen(false);
        return;
      }
      setEmailChecking(true);
      try {
        await apiClient("/email-exists", "POST", { email });
        setEmailExists(false);
      } catch (e) {
        if (e?.detail === "Email already exists") {
          setEmailDialogOpen(true);
          setEmailExists(true);
        } else {
          setEmailExists(false);
        }
      } finally {
        setEmailChecking(false);
      }
    }, EMAIL_DEBOUNCE_TIMEOUT);
  }

  // Dialog handlers
  const handleDialogClose = () => {
    setEmailDialogOpen(false);
  };
  const handleDialogContinue = () => {
    setEmailDialogOpen(false);
    setEmailExists(false);
    // Keep the email as is, allow user to proceed
  };
  const handleDialogCancel = () => {
    setEmailDialogOpen(false);
    setValue("email", "");
    setEmailExists(false);
  };

  // Fetch cities, states, and obligees
  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        const [cities, states, obligeesRes] = await Promise.all([
          apiClient("/fields/city/", "GET"),
          apiClient("/fields/states/", "GET"),
          apiClient("/obligee-list-create/"),
        ]);

        setAllCities(cities);
        setAllStates(states);
        setObligees(obligeesRes);

        // Set default obligee if exists
        const defaultObligee = obligeesRes.find(
          (o) => o.obligee_name === "Governor of Florida (Ron Desantis)"
        );
        if (defaultObligee) {
          setValue("obligeeId", defaultObligee.obligee_id);
        }

        scrollToTop();
      } catch (error) {
        console.error("Error loading data:", error);
        toast.error("Failed to load data");
      }
    };

    loadDropdownData();

    const savedState = localStorage.getItem("selectedState");
    if (savedState) {
      const parsedState = JSON.parse(savedState);
      setState(parsedState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch counties when state changes
  useEffect(() => {
    const fetchCounties = async () => {
      if (state && state.id) {
        setLoadingCounties(true);
        try {
          const countyResponse = await apiClient(
            `/counties-by-state/${state.id}/`,
            "GET"
          );
          const formattedCounties = countyResponse.map(
            (county) => county.bond_county
          );
          setAllCounties(formattedCounties);
        } catch (error) {
          console.error("Error fetching counties:", error);
        } finally {
          setLoadingCounties(false);
        }
      }
    };
    fetchCounties();
  }, [state]);

  // Handle law firm user case
  useEffect(() => {
    const fetchLawFirm = async () => {
      if (role === "law_firm_user" || role === "law_firm_admin") {
        const lawfirm_id = lawfirmState.lawfirmId;
        setLawfirm(lawfirm_id);
      }
    };
    fetchLawFirm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  // Handle professional guardian case
  useEffect(() => {
    if (role === null) {
      setProfGuardianRequestor(pgId);
    }
  }, [role, pgId]);

  const handleOtherResidencyChange = (e) => {
    const value = e.target.value;
    setOtherResidency(value);
    setValue("residencyStatus", value);
  };

  const formatSSN = (ssn) => {
    if (ssn.length < 4) return ssn;
    return "XXX-XX-" + ssn.slice(-4);
  };

  const handleGoogleCallback = (address, city, state, zipCode) => {
    setValue("address", address);
    setValue("city", city);
    setValue("state", state);
    setValue("zipCode", zipCode);
  };

  const handleObligeeGoogleCallback = (address, city, state, zipCode) => {
    setNewObligee((prev) => ({
      ...prev,
      street: address,
      city:
        allCities.find((c) => c.toLowerCase() === city.toLowerCase()) || city,
      state:
        allStates.find((s) => s.toLowerCase() === state.toLowerCase()) || state,
      zip: zipCode,
    }));
  };

  useEffect(() => {
    const path = location.pathname || "";
    setIsGuestBlanketRequest(path.startsWith("/vermost-blanket-bond-request-guest"));
  }, [location.pathname]);

  const handleAddObligee = async () => {
    try {
      setLoadingObligees(true);
      const response = await apiClient("/obligee-list-create/", "POST", {
        obligee_name: newObligee.name,
        obligee_address: newObligee.street,
        obligee_city: newObligee.city,
        obligee_state: newObligee.state,
        obligee_zip: newObligee.zip,
      });

      setObligees((prev) => [...prev, response]);
      setValue("obligeeId", response.obligee_id);
      setOpenObligeeDialog(false);
      setNewObligee({ name: "", street: "", city: "", state: "", zip: "" });
      toast.success("Obligee added successfully!");
    } catch (error) {
      console.error("Error adding obligee:", error);
      toast.error("Failed to add obligee");
    } finally {
      setLoadingObligees(false);
    }
  };

  const handleNext = async (event) => {
    event.preventDefault();
    const isStepValid = await trigger(); // Triggers validation
    if (!isStepValid) return; // Stop if validation fails
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = (event) => {
    event.preventDefault();
    setActiveStep((prevStep) => prevStep - 1);
  };

  const calculatePremium = (bondTerm) => {
    switch (bondTerm) {
      case "1 year Bond":
        return 175.0;
      case "2 year Bond":
        return 306.0;
      case "3 year Bond":
        return 437.0;
      default:
        return 175.0;
    }
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    let payload = {};
    try {
      payload = {
        bond: {
          bond_carrier: is_prod ? 7 : 4,
          bond_type: 5,
          bond_sub_category: data.bondTerm,
          bond_amount: 50000.0,
          premium: calculatePremium(data.bondTerm),
          state: 6,
          county: data.county,
          is_esignatures: data.is_esignatures === "Yes",
          is_guest: isGuestBlanketRequest,
          ...(isGuestBlanketRequest ? (() => {
            try { const r = JSON.parse(localStorage.getItem("guestReferralData")); return r ? { referral_source: r.referral_source || null, referred_by: r.referred_by || null } : {}; } catch { return {}; }
          })() : {}),
          status: "Active",
          name: `${data.firstName} ${data.lastName}`,
          law_firm: lawfirm,
          requester_id: isGuestBlanketRequest ? null : userdetails.id,
          managing_lawfirm: lawfirmState.lawfirmId,
          requester_name: isGuestBlanketRequest ? "Guest" : `${userdetails.first_name} ${userdetails.last_name}`,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          obligee: data.obligeeId,
        },
        blanketbond: {
          first_name: data.firstName,
          middle_name: data.middleName,
          last_name: data.lastName,
          preferred_name: data.preferredName,
          street_address_line_1: data.address,
          street_address_line_2: "",
          city: data.city,
          state: data.state,
          zip: data.zipCode,
          cell_phone: data.cell_phone,
          should_we_text: data.mayWeText === "Yes",
          email: data.email,
          dob: data.dob || null,
          date_of_expiration: data.doe || null,
          ssn: data.ssn,
          is_us_citizen: data.usCitizen === "Yes",
          residency_status: data.residencyStatus,
          bankrupcy: data.bankrupt === "Yes",
          bankrupcy_explanation: data.bankruptExplanation,
          judgements: data.lawsuits === "Yes",
          judgements_explanation: data.lawsuitsExplanation,
          is_decline: data.declinedSurety === "Yes",
          declined_explanation: data.suretyExplanation,
          authorization_underwriting: data.authorizationAgreement,
          is_credit_report: data.authorizationAgreement,
          is_replace: data.replacingBond === "Yes",
          surety_name_reason: data.bondReplacementReason,
          bond_term: data.bondTerm,
          pg_id: profGuardianRequestor,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      };

      const response = await apiClient("/create-blanket-bond/", "POST", payload);
      localStorage.removeItem("guestReferralData");
      toast.success("Bond created successfully!");
      // Queue a cross-page success toast on the destination home
      try {
        const bondId =
          response?.bond_id ||
          response?.id ||
          response?.bond?.bond_id ||
          response?.data?.bond_id;
        if (bondId) {
          sessionStorage.setItem(
            "flashToast",
            JSON.stringify({
              message: `Your Recent Bond Request #${bondId} was Submitted!`,
              link: `/vermost-blanket-bond-update/${bondId}`,
            })
          );
        }
      } catch (_) { }
      setIsLoading(false);
      reset();
      setActiveStep(0);

      const rolePathMap = {
        super_user: "/admin_home",
        internal_user: "/internal_user_home",
        law_firm_user: "/law_firm_user_home",
        law_firm_admin: "/law_firm_admin_home",
        carrier_team_member: "/carrier_team_member_dashboard",
        "professional guardian": "/professional_guardian_home",
        pg_contact: "/pg_contact_home",
      };
      navigate(rolePathMap[role]);
    } catch (error) {
      const message = extractErrorMessage(error);
      console.error("Error submitting form:", error);

      setSubmissionError(message);
      setFailedBondType("Blanket");
      setSupportModalOpen(true);

      setIsLoading(false);
    }
  };

  const handleSupportRequestSubmit = async (description) => {
    try {
      const values = getValues();
      const hydrationPayload = {
        activeStep,
        state: values,
      };

      await createSupportRequest({
        request_body: JSON.stringify(hydrationPayload),
        bond_type: failedBondType,
        name: `${values.firstName || ""} ${values.lastName || ""}`.trim() || "Unknown",
        user_comment: description
      });
      toast.success("Support request created successfully.");
      setSupportModalOpen(false);

      const rolePathMap = {
        super_user: "/admin_home",
        internal_user: "/internal_user_home",
        law_firm_user: "/law_firm_user_home",
        law_firm_admin: "/law_firm_admin_home",
        carrier_team_member: "/carrier_team_member_dashboard",
        "professional guardian": "/professional_guardian_home",
        pg_contact: "/pg_contact_home",
      };
      navigate(rolePathMap[role] || "/");
    } catch (error) {
      console.error("Failed to create support request", error);
      toast.error("Failed to submit support request.");
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const requester_id = userdetails?.id;
      if (!requester_id) {
        toast.error("Unable to save: Login to save your progress");
        setIsSaving(false);
        return;
      }

      const values = getValues();
      const name = `${values.firstName || ""} ${values.lastName || ""}`.trim();

      const saved = await apiClient(`/bond-cache/`, "POST", {
        cache_id: cacheId || cache_id,
        requester_id,
        bond_type: "Blanket",
        name: name || "Untitled Blanket Bond",
        request_body: JSON.stringify({
          activeStep,
          state: values,
        }),
      });

      const returnedId = saved?.bond_cache_id || saved?.cache_id || saved?.id;
      if (returnedId) {
        if (returnedId !== cacheId) setCacheId(returnedId);
        if (!cache_id) {
          navigate(`/vermost-blanket-bond-request-cache/${returnedId}`, { replace: true });
        }
      }

      toast.success("Progress saved");
    } catch (error) {
      toast.error(
        (error && (error.detail || error.error)) || "Failed to save progress"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const BasicInformation = () => (
    <Box className="grid grid-cols-1 gap-4">
      <Typography variant="h5" className="font-bold mb-4">
        Basic Information
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Controller
            name="firstName"
            control={control}
            defaultValue=""
            rules={{ required: "First name is required" }} // Add validation rule
            render={({ field }) => (
              <TextField
                {...field}
                label="First Name"
                fullWidth
                required
                variant="outlined"
                error={!!errors.firstName}
                helperText={errors.firstName ? "First Name is required" : ""}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name="middleName"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label="Middle Name"
                fullWidth
                variant="outlined"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name="lastName"
            control={control}
            defaultValue=""
            rules={{ required: "Last name is required" }} // Add validation rule
            render={({ field }) => (
              <TextField
                {...field}
                label="Last Name"
                fullWidth
                required
                variant="outlined"
                error={!!errors.lastName}
                helperText={errors.lastName ? "Last Name is required" : ""}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name="preferredName"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label="Preferred Name"
                fullWidth
                variant="outlined"
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="dob"
            control={control}
            defaultValue=""
            rules={{ required: "Date of birth is required" }} // Add validation rule
            render={({ field }) => (
              <TextField
                {...field}
                label="Date of Birth"
                type="date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                variant="outlined"
                required
                error={!!errors.dob}
                helperText={errors.dob ? "Date of Birth is required" : ""}
                inputProps={{
                  max: new Date().toISOString().split('T')[0]
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="county"
            control={control}
            defaultValue=""
            rules={{ required: "County is required" }} // Add validation rule
            render={({ field }) => (
              <Autocomplete
                {...field}
                options={allCounties}
                getOptionLabel={(option) => option || ""}
                loading={loadingCounties}
                onChange={(event, newValue) => {
                  setValue("county", newValue ? newValue : "");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Home County"
                    fullWidth
                    required
                    variant="outlined"
                    error={!!errors.county}
                    helperText={errors.county ? "County is required" : ""}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingCounties ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
          />
        </Grid>
        {residencyStatus === "Other" && (
          <Grid item xs={12}>
            <TextField
              label="Please specify"
              fullWidth
              variant="outlined"
              value={otherResidency}
              onChange={handleOtherResidencyChange}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <Typography
            sx={{
              fontSize: "12px",
              color: "rgba(0, 0, 0, 0.6)",
              marginBottom: "4px",
              fontWeight: 500,
            }}
          >
            Search Address
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <GoogleAutoComplete
              placeholder="Search Address"
              callback={handleGoogleCallback}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="address"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label="Street Address"
                fullWidth
                variant="outlined"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="zipCode"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label="ZIP/Postal Code"
                fullWidth
                variant="outlined"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="city"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Autocomplete
                {...field}
                options={allCities}
                getOptionLabel={(option) => option}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="City"
                    fullWidth
                    variant="outlined"
                    error={!!errors.city}
                    helperText={errors.city ? "City is required" : ""}
                  />
                )}
                onChange={(event, value) => field.onChange(value)}
              />
            )}
            rules={{ required: true }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name="state"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Autocomplete
                {...field}
                options={allStates}
                getOptionLabel={(option) => option}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="State"
                    fullWidth
                    variant="outlined"
                    error={!!errors.state}
                    helperText={errors.state ? "State is required" : ""}
                  />
                )}
                onChange={(event, value) => field.onChange(value)}
              />
            )}
            rules={{ required: true }}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="email"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <>
                <TextField
                  {...field}
                  label="Email"
                  fullWidth
                  required
                  variant="outlined"
                  error={!!errors.email || emailExists}
                  helperText={
                    emailChecking
                      ? "Checking email..."
                      : emailExists
                        ? "This email already exists."
                        : errors.email
                          ? "Email is required"
                          : ""
                  }
                  onChange={e => {
                    field.onChange(e);
                    emailDebounceRef.current(e.target.value, setValue);
                  }}
                />
                {/* Email Exists Dialog */}
                <Dialog
                  open={emailDialogOpen}
                  onClose={(_, reason) => {
                    if (reason !== 'backdropClick') handleDialogClose();
                  }}
                >
                  <DialogTitle>Warning</DialogTitle>
                  <DialogContent>
                    <DialogContentText>
                      This email already exists for an existing user in the Probate Bond Pros System. Would you like to continue with this email?
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleDialogCancel} color="secondary">Cancel</Button>
                    <Button onClick={handleDialogContinue} color="primary" autoFocus>
                      Continue
                    </Button>
                  </DialogActions>
                </Dialog>
              </>
            )}
            rules={{ required: true }}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography>Will you prefer to e-sign the Indemnity Agreement?</Typography>
          <Controller
            name="is_esignatures"
            control={control}
            defaultValue="No"
            render={({ field }) => (
              <RadioGroup {...field} row>
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            )}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Typography>May we text you?</Typography>
          <Controller
            name="mayWeText"
            control={control}
            defaultValue="Yes"
            render={({ field }) => (
              <RadioGroup {...field} row>
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            )}
          />
        </Grid>
        <Grid item xs={12} sm={9}>
          <Controller
            name="cell_phone"
            control={control}
            defaultValue=""
            rules={{ required: true }}
            render={({ field }) => (
              <InputMask
                mask="999-999-9999"
                value={field.value}
                onChange={field.onChange}
              >
                {(inputProps) => (
                  <TextField
                    {...inputProps}
                    label="Cell Phone"
                    fullWidth
                    required
                    variant="outlined"
                    placeholder="123-456-7890"
                    error={!!errors.cell_phone}
                    helperText={
                      errors.cell_phone ? "Cell Phone is required" : ""
                    }
                  />
                )}
              </InputMask>
            )}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Typography>Are you a U.S. Citizen?</Typography>
          <Controller
            name="usCitizen"
            control={control}
            defaultValue="Yes"
            render={({ field }) => (
              <RadioGroup {...field} row>
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            )}
          />
        </Grid>
        <Grid item xs={12} md={9}>
          <Controller
            name="ssn"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label="Social Security Number"
                type="password"
                fullWidth
                required
                variant="outlined"
                error={!!errors.ssn}
                helperText={
                  errors.ssn ? "SSN is required" : formatSSN(field.value)
                }
              />
            )}
            rules={{ required: true }}
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Box
          sx={{
            border: "1px solid #d1d5db", // Light gray border
            borderRadius: "8px", // Rounded corners
            backgroundColor: "#f9fafb", // Light background
            padding: "16px", // Add spacing inside the box
            margin: "16px 0", // Add vertical spacing outside the box
          }}
        >
          <Typography variant="h6" sx={{ marginBottom: "2px" }}>
            Authorization to Obtain Underwriting Information
          </Typography>
          <Typography>
            Do you fully understand and agree, by submitting this request for
            approval you are providing authorization for Probate Bond Pros,
            surety companies and/or third-party brokers to verify information
            you provided as well as permission to obtain your consumer
            information from any source which would be necessary to determine
            bonding eligibility? This includes obtaining credit reports at time
            of application, during any review or renewal, at the time of any
            potential or actual claim, and/or for any other legitimate purposes
            determined by the surety in its reasonable discretion. This
            information will be held in the strictest confidence. Do you agree?
          </Typography>

          <Controller
            name="authorizationAgreement"
            control={control}
            rules={{ required: "You must select an option" }}
            render={({ field }) => (
              <FormControl
                error={!!errors.authorizationAgreement}
                component="fieldset"
              >
                <RadioGroup {...field} row>
                  <FormControlLabel
                    value={true}
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value={false}
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
                {errors.authorizationAgreement && (
                  <FormHelperText error>
                    {errors.authorizationAgreement.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Box>
      </Grid>
    </Box>
  );

  const DetailedInformation = () => (
    <Box className="grid grid-cols-1 gap-4">
      <Typography variant="h5" className="font-bold mb-4">
        Detailed Information
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography>Have you been bankrupt or insolvent?</Typography>
          <Controller
            name="bankrupt"
            control={control}
            defaultValue="No"
            render={({ field }) => (
              <RadioGroup {...field} row>
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            )}
          />
        </Grid>

        {watch("bankrupt") === "Yes" && (
          <Grid item xs={12}>
            <Controller
              name="bankruptExplanation"
              control={control}
              defaultValue=""
              rules={{ required: "Explanation is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Provide an explanation (Year)"
                  fullWidth
                  variant="outlined"
                  error={!!errors.bankruptExplanation}
                  helperText={errors.bankruptExplanation?.message}
                />
              )}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <Typography>
            Any lawsuits, judgments, or liens against you?
          </Typography>
          <Controller
            name="lawsuits"
            control={control}
            defaultValue="No"
            render={({ field }) => (
              <RadioGroup {...field} row>
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            )}
          />
        </Grid>

        {watch("lawsuits") === "Yes" && (
          <Grid item xs={12}>
            <Controller
              name="lawsuitsExplanation"
              control={control}
              defaultValue=""
              rules={{ required: "Explanation is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Provide an explanation"
                  fullWidth
                  variant="outlined"
                  error={!!errors.lawsuitsExplanation}
                  helperText={errors.lawsuitsExplanation?.message}
                />
              )}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <Typography>
            Has application for this bond been declined by another surety?
          </Typography>
          <Controller
            name="declinedSurety"
            control={control}
            defaultValue="No"
            render={({ field }) => (
              <RadioGroup {...field} row>
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            )}
          />
        </Grid>

        {watch("declinedSurety") === "Yes" && (
          <Grid item xs={12}>
            <Controller
              name="suretyExplanation"
              control={control}
              defaultValue=""
              rules={{ required: "Explanation is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Provide an explanation"
                  fullWidth
                  variant="outlined"
                  error={!!errors.suretyExplanation}
                  helperText={errors.suretyExplanation?.message}
                />
              )}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <Typography>Is this bond replacing a previous bond?</Typography>
          <Controller
            name="replacingBond"
            control={control}
            defaultValue="No"
            render={({ field }) => (
              <RadioGroup {...field} row>
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            )}
          />
        </Grid>

        {watch("replacingBond") === "Yes" && (
          <>
            <Grid item xs={12}>
              <Controller
                name="bondReplacementReason"
                control={control}
                defaultValue=""
                rules={{ required: "Explanation is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Provide surety name and reason for change"
                    fullWidth
                    variant="outlined"
                    error={!!errors.bondReplacementReason}
                    helperText={errors.bondReplacementReason?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="doe"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Date of Expiration of Current Bond"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    variant="outlined"
                  />
                )}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <Typography>Which bond term would you like?</Typography>
          <Controller
            name="bondTerm"
            control={control}
            defaultValue="1 Year Bond"
            render={({ field }) => (
              <RadioGroup {...field} row>
                <FormControlLabel
                  value="1 Year Bond"
                  control={<Radio />}
                  label={
                    <>
                      <Typography component="span" sx={{ fontWeight: "bold" }}>
                        $175.00
                      </Typography>{" "}
                      (1 Year Bond)
                    </>
                  }
                />
                <FormControlLabel
                  value="2 Year Bond"
                  control={<Radio />}
                  label={
                    <>
                      <Typography component="span" sx={{ fontWeight: "bold" }}>
                        $306.00
                      </Typography>{" "}
                      (2 Year Bond)
                    </>
                  }
                />
                <FormControlLabel
                  value="3 Year Bond"
                  control={<Radio />}
                  label={
                    <>
                      <Typography component="span" sx={{ fontWeight: "bold" }}>
                        $437.00
                      </Typography>{" "}
                      (3 Year Bond)
                    </>
                  }
                />
              </RadioGroup>
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );

  const ObligeeInformation = () => (
    <Box className="grid grid-cols-1 gap-4">
      <Typography variant="h5" className="font-bold mb-4">
        Obligee Information
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Controller
            name="obligeeId"
            control={control}
            render={({ field }) => (
              <Autocomplete
                options={obligees}
                getOptionLabel={(option) => option.obligee_name || ""}
                loading={loadingObligees}
                value={
                  obligees.find((o) => o.obligee_id === field.value) || null
                }
                onChange={(event, newValue) => {
                  field.onChange(newValue ? newValue.obligee_id : null);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Obligee"
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingObligees ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="outlined"
            onClick={() => setOpenObligeeDialog(true)}
            disabled={loadingObligees}
            fullWidth
          >
            {loadingObligees ? (
              <CircularProgress size={24} />
            ) : (
              "Add New Obligee"
            )}
          </Button>
        </Grid>

        {watch("obligeeId") && (
          <Grid item xs={12}>
            <Box
              sx={{
                mt: 2,
                p: 2,
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                backgroundColor: "#f9f9f9",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold", mb: 1 }}
              >
                Selected Obligee:
              </Typography>
              {(() => {
                const selected = obligees.find(
                  (o) => o.obligee_id === watch("obligeeId")
                );
                return selected ? (
                  <>
                    <Typography sx={{ mb: 1 }}>
                      <Box component="span" sx={{ fontWeight: "bold" }}>
                        Name:
                      </Box>{" "}
                      {selected.obligee_name}
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                      <Box component="span" sx={{ fontWeight: "bold" }}>
                        Address:
                      </Box>{" "}
                      {selected.obligee_address}
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                      <Box component="span" sx={{ fontWeight: "bold" }}>
                        City:
                      </Box>{" "}
                      {selected.obligee_city}
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                      <Box component="span" sx={{ fontWeight: "bold" }}>
                        State:
                      </Box>{" "}
                      {selected.obligee_state}
                    </Typography>
                    <Typography>
                      <Box component="span" sx={{ fontWeight: "bold" }}>
                        ZIP:
                      </Box>{" "}
                      {selected.obligee_zip}
                    </Typography>
                  </>
                ) : null;
              })()}
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Add New Obligee Dialog */}
      <Dialog
        open={openObligeeDialog}
        onClose={() => setOpenObligeeDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add New Obligee</DialogTitle>
        <DialogContent>
          <TextField
            label="Obligee Name"
            value={newObligee.name}
            onChange={(e) =>
              setNewObligee({ ...newObligee, name: e.target.value })
            }
            fullWidth
            margin="normal"
            required
            sx={{ mb: 2 }}
          />

          <GoogleAutoComplete
            placeholder="Search Address"
            callback={handleObligeeGoogleCallback}
          />

          <TextField
            label="Street Address"
            value={newObligee.street}
            onChange={(e) =>
              setNewObligee({ ...newObligee, street: e.target.value })
            }
            fullWidth
            margin="normal"
            required
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <Autocomplete
              options={allCities}
              value={newObligee.city}
              onChange={(e, value) =>
                setNewObligee({ ...newObligee, city: value })
              }
              renderInput={(params) => (
                <TextField {...params} label="City" fullWidth required />
              )}
              sx={{ flex: 1 }}
            />

            <Autocomplete
              options={allStates}
              value={newObligee.state}
              onChange={(e, value) =>
                setNewObligee({ ...newObligee, state: value })
              }
              renderInput={(params) => (
                <TextField {...params} label="State" fullWidth required />
              )}
              sx={{ flex: 1 }}
            />
          </Box>

          <TextField
            label="ZIP Code"
            value={newObligee.zip}
            onChange={(e) =>
              setNewObligee({ ...newObligee, zip: e.target.value })
            }
            fullWidth
            margin="normal"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenObligeeDialog(false)}>Cancel</Button>
          <Button
            onClick={handleAddObligee}
            variant="contained"
            disabled={
              !newObligee.name ||
              !newObligee.street ||
              !newObligee.city ||
              !newObligee.state ||
              !newObligee.zip
            }
          >
            Add Obligee
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  return (
    <>
      {isGuestBlanketRequest ? <GuestNavbar /> : <Navbar />}
      <Container maxWidth="md" sx={{ mt: 6, mb: 6, pb: isGuestBlanketRequest ? 10 : 0 }}>
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{ fontWeight: "bold" }}
        >
          Guardianship Blanket Bond Request
        </Typography>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Paper
          elevation={3}
          sx={{ p: 4, backgroundColor: "#f5f5f5", borderRadius: "15px" }}
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            {activeStep === 0 && <BasicInformation />}
            {activeStep === 1 && <DetailedInformation />}
            {activeStep === 2 && <ObligeeInformation />}

            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              {activeStep > 0 && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleBack}
                  sx={{ mr: 2 }}
                  type="button"
                >
                  Back
                </Button>
              )}
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleSave}
                sx={{ mx: 2 }}
                endIcon={isSaving && <CircularProgress size={20} />}
                disabled={isSaving}
                type="button"
              >
                {isSaving ? "Saving..." : "Save"}
              </Button>
              {activeStep === steps.length - 1 ? (
                <Button
                  disabled={isLoading}
                  endIcon={
                    isLoading && <CircularProgress size={20} color="inherit" />
                  }
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Submit
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                >
                  Next
                </Button>
              )}
            </Box>
          </form>
        </Paper>
        <SupportRequestModal
          open={supportModalOpen}
          onClose={() => setSupportModalOpen(false)}
          onSubmit={handleSupportRequestSubmit}
          errorMessage={submissionError}
        />
      </Container>

      {isGuestBlanketRequest && (
        <>
          {/* Bottom Tabs */}
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1000,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              pr: 2,
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {/* Support Tab */}
            <Box
              onClick={() => setOpenSupport(true)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                backgroundColor: "#D3D3D3",
                color: "#333",
                px: 4,
                py: 2,
                borderRadius: "8px 8px 0 0",
                cursor: "pointer",
                minWidth: "300px",
                maxWidth: "400px",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "#C8C8C8",
                  transform: "translateY(-2px)",
                  boxShadow: "0 -6px 16px rgba(0, 0, 0, 0.2)",
                },
                "@media (max-width: 1024px)": {
                  minWidth: "260px",
                  maxWidth: "340px",
                  px: 3,
                  py: 1.5,
                  gap: 1.5,
                },
                "@media (max-width: 768px)": {
                  minWidth: "220px",
                  maxWidth: "300px",
                  px: 2,
                  py: 1,
                  gap: 1,
                },
              }}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  backgroundColor: "#333",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "2px",
                  flexShrink: 0,
                  "@media (max-width: 1024px)": {
                    width: 18,
                    height: 18,
                  },
                  "@media (max-width: 768px)": {
                    width: 16,
                    height: 16,
                  },
                }}
              >
                <EmailIcon
                  sx={{
                    fontSize: 14,
                    color: "white",
                    "@media (max-width: 1024px)": {
                      fontSize: 12,
                    },
                    "@media (max-width: 768px)": {
                      fontSize: 10,
                    },
                  }}
                />
              </Box>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1rem",
                  fontWeight: 500,
                  color: "#333",
                  whiteSpace: "nowrap",
                  "@media (max-width: 1024px)": {
                    fontSize: "0.9rem",
                  },
                  "@media (max-width: 768px)": {
                    fontSize: "0.8rem",
                  },
                }}
              >
                Contact Support
              </Typography>
            </Box>

            {/* Calculator Tab */}
            <Box
              onClick={() => setOpenCalculator(true)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                backgroundColor: "#D3D3D3",
                color: "#333",
                px: 4,
                py: 2,
                borderRadius: "8px 8px 0 0",
                cursor: "pointer",
                minWidth: "320px",
                maxWidth: "400px",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "#C8C8C8",
                  transform: "translateY(-2px)",
                  boxShadow: "0 -6px 16px rgba(0, 0, 0, 0.2)",
                },
                "@media (max-width: 1024px)": {
                  minWidth: "280px",
                  maxWidth: "350px",
                  px: 3,
                  py: 1.5,
                  gap: 1.5,
                },
                "@media (max-width: 768px)": {
                  minWidth: "240px",
                  maxWidth: "300px",
                  px: 2,
                  py: 1,
                  gap: 1,
                },
              }}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  backgroundColor: "#333",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "2px",
                  flexShrink: 0,
                  "@media (max-width: 1024px)": {
                    width: 18,
                    height: 18,
                  },
                  "@media (max-width: 768px)": {
                    width: 16,
                    height: 16,
                  },
                }}
              >
                <CalculatorIcon
                  sx={{
                    fontSize: 14,
                    color: "white",
                    "@media (max-width: 1024px)": {
                      fontSize: 12,
                    },
                    "@media (max-width: 768px)": {
                      fontSize: 10,
                    },
                  }}
                />
              </Box>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1rem",
                  fontWeight: 500,
                  color: "#333",
                  whiteSpace: "nowrap",
                  "@media (max-width: 1024px)": {
                    fontSize: "0.9rem",
                  },
                  "@media (max-width: 768px)": {
                    fontSize: "0.8rem",
                  },
                }}
              >
                Use our Bond Premium Calculator
              </Typography>
            </Box>
          </Box>

          <SupportContactDialog
            open={openSupport}
            onClose={() => setOpenSupport(false)}
            context="Guest Blanket Bond Request"
          />

          <Dialog
            open={openCalculator}
            onClose={() => setOpenCalculator(false)}
            sx={{ "& .MuiPaper-root": { borderRadius: "20px", maxWidth: "90vw" } }}
          >
            <DialogContent>
              <PremiumCalculator />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setOpenCalculator(false)}
                sx={{
                  borderRadius: "12px",
                  color: "#333333",
                  fontWeight: 600,
                  "&:hover": { backgroundColor: "rgba(51, 51, 51, 0.08)" },
                  textTransform: "none",
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </>
  );
};

export default VermostBlanketBondRequestForm;
